import React, { useState, useEffect, useRef } from 'react';
import { Container, Table, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import api, { getInventoryByLot } from '../services/api';
import useDebounce from '../hooks/useDebounce';

function Catalog() {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const observerTarget = useRef(null);
  const { user } = useAuth();

  // Fetch catalog data
  const fetchCatalog = async (page = 1, reset = false) => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.customerId) {
        setError('User data not available. Please try logging in again.');
        setLoading(false);
        return;
      }

      const response = await getInventoryByLot(page, 50, debouncedSearchTerm);
      const items = response?.items || [];
      const pagination = response?.pagination || { total: 0, totalPages: 1 };

      // Group lots by SKU and aggregate
      const groupedData = items.reduce((acc, lot) => {
        const key = lot.itemNumber;
        if (!acc[key]) {
          acc[key] = {
            itemNumber: lot.itemNumber,
            itemName: lot.itemName,
            onHand: 0,
            availQty: 0,
            tap: 0,
            issued90Days: 0,
            skuTotalInventory: 0,
            ageDaySum: 0,
            lotCount: 0,
            lots: []
          };
        }
        const onHandQty = Number(lot.onHand || 0);
        const availableQty = Number(lot.availQty || 0);
        const ppuValue = Number(lot.ppu || 1);

        acc[key].onHand += onHandQty;
        acc[key].availQty += availableQty;
        acc[key].tap += availableQty * ppuValue;

        // SKU-level values — same for every lot of this SKU
        acc[key].issued90Days = Number(lot.issued_90_days || 0);
        acc[key].skuTotalInventory = Number(lot.sku_total_inventory || 0);

        // Accumulate age for true average (backend gives ageInDays per lot)
        acc[key].ageDaySum += Number(lot.ageInDays || 0);
        acc[key].lotCount += 1;

        acc[key].lots.push(lot);
        return acc;
      }, {});

      // Compute SKU-level metrics
      Object.values(groupedData).forEach(sku => {
        // Turnover: issues in 90 days / total inventory on hand
        sku.turnover90 = sku.skuTotalInventory > 0
          ? (sku.issued90Days / sku.skuTotalInventory).toFixed(2)
          : '0.00';

        // True average age across all lots
        sku.avgAge = sku.lotCount > 0
          ? Math.round(sku.ageDaySum / sku.lotCount)
          : 0;

        // Days on hand: current inventory / daily usage rate
        const dailyUsage = sku.issued90Days / 90;
        sku.avgDaysOnHand = dailyUsage > 0
          ? Math.round(sku.skuTotalInventory / dailyUsage)
          : 0;
      });

      const newData = Object.values(groupedData);
      
      if (reset) {
        setCatalog(newData);
      } else {
        setCatalog(prev => [...prev, ...newData]);
      }
      
      setTotalItems(pagination.total);
      setHasMore(page < pagination.totalPages);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching catalog:', err);
      setError('Failed to load catalog data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Single effect: fires on mount (when user is ready) and on search changes
  useEffect(() => {
    if (!user) return;
    if (!user.customerId) {
      setLoading(false);
      setError('User ID not available. Please try logging in again.');
      return;
    }
    setCurrentPage(1);
    fetchCatalog(1, true);
  }, [user?.customerId, debouncedSearchTerm]);

  // Setup infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchCatalog(currentPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, currentPage]);

  // Handle search
  const filteredCatalog = catalog;

  // Export to Excel
  const handleExport = async () => {
    try {
      setLoading(true);
      // Fetch all data for export (no pagination limit)
      const response = await getInventoryByLot(1, 999999, debouncedSearchTerm);
      const allItems = response?.items || [];
      
      const exportData = allItems.map(lot => ({
        'Item Number': lot.itemNumber,
        'Item Name': lot.itemName,
        'Lot ID': lot.idLevels,
        'Age (days)': lot.ageInDays || 0,
        'Days on Hand': lot.daysOnHand < 999 ? lot.daysOnHand : 'N/A',
        'Turnover (90d)': lot.turnover90 || '0.00',
        'PPU': lot.ppu || 1,
        'Units': lot.onHand,
        'Available': lot.availQty,
        'TAP': (lot.availQty || 0) * (lot.ppu || 1),
        'Notes': lot.notes || ''
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Catalog');
      XLSX.writeFile(wb, 'catalog_export.xlsx');
    } catch (err) {
      console.error('Error exporting catalog:', err);
      setError('Failed to export catalog data.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && catalog.length === 0) {
    return (
      <Container fluid className="p-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <Row className="mb-4 align-items-center">
        <Col md={3}>
          <h2 className="mb-0">Catalog Management</h2>
        </Col>
        <Col md={6}>
          <Form.Control
            type="text"
            placeholder="Search catalog..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md={3} className="text-end">
          <Button
            variant="success"
            onClick={handleExport}
            className="me-2"
          >
            <FontAwesomeIcon icon={faFileExcel} className="me-2" />
            Export to Excel
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)', borderRadius: '8px', border: '1px solid #e9ecef' }}>
        <Table bordered hover className="mb-0">
          <thead>
            <tr>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>Item Number</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>Item Name</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }} className="text-end" title="Average age in days since receipt">Avg Age</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }} className="text-end" title="Days of inventory remaining at current usage rate">Days on Hand</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }} className="text-end" title="90-day turnover rate">Turnover (90d)</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }} className="text-end">TAP</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }} className="text-end">Units</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }} className="text-end">Available</th>
            </tr>
          </thead>
          <tbody>
            {filteredCatalog.map(item => (
              <tr key={item.itemNumber}>
                <td>{item.itemNumber}</td>
                <td>{item.itemName}</td>
                <td className="text-end" style={{
                  backgroundColor: item.avgAge > 180 ? '#ffe6e6' : item.avgAge > 90 ? '#fff4e6' : 'transparent'
                }}>
                  {item.avgAge} days
                </td>
                <td className="text-end" style={{
                  backgroundColor: item.avgDaysOnHand > 0 ? (item.avgDaysOnHand < 30 ? '#ffe6e6' : item.avgDaysOnHand < 60 ? '#fff4e6' : '#e6f4ea') : 'transparent'
                }}>
                  {item.avgDaysOnHand > 0 ? `${item.avgDaysOnHand} days` : 'N/A'}
                </td>
                <td className="text-end" style={{
                  backgroundColor: Number(item.turnover90) > 1 ? '#e6f4ea' : Number(item.turnover90) > 0.5 ? '#fff4e6' : '#ffe6e6'
                }}>
                  {item.turnover90}
                </td>
                <td className="text-end">{item.tap.toLocaleString()}</td>
                <td className="text-end">{item.onHand.toLocaleString()}</td>
                <td className="text-end">{item.availQty.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        {/* Infinite scroll trigger */}
        <div 
          ref={observerTarget}
          style={{ height: '20px', width: '100%' }}
        >
          {loading && (
            <div className="text-center py-2">
              <Spinner animation="border" size="sm" />
            </div>
          )}
        </div>
      </div>
    </Container>
  );
}

export default Catalog; 